import {
  FINAL_OUTCOMES,
  type FinalOutcome,
  HITL_REASONS,
  type HitlReason,
} from "@/types/tina";

/** One entry of a `filterType: "select"` column filter. */
interface FilterOption<T> {
  label: string;
  value: T;
}

export type HitlEntityType =
  | "venue"
  | "artist"
  | "event"
  | "movie"
  | "play"
  | "person"
  | "image"
  // All three enrichment resolution kinds collapse to one entity type on
  // purpose — `enrichment_low_confidence` is a single reason covering five
  // entity flows.
  | "enrichment"
  // The refresh family. Tina's `_entity_type_for` and
  // `entity_type_for_resolution` both answer `"refresh"` for `refresh_review` /
  // `refresh.review`, and `resolve_hitl` cross-checks the two, so this string is
  // a wire contract and not just a label key.
  | "refresh";

// Hardcoded for v1 (spec §6.5). Revisit when /stages endpoint lands.
export const TINA_STAGES = [
  "discover",
  "score",
  "lineup",
  "venue",
  "artist",
  "signals",
] as const;
export type TinaStage = (typeof TINA_STAGES)[number];

// Per handoff §4.4 — derives entity type from hitl_reason.
export const HITL_REASON_TO_ENTITY_TYPE: Record<HitlReason, HitlEntityType> = {
  venue_no_address: "venue",
  venue_places_miss: "venue",
  venue_low_confidence: "venue",
  venue_geodata_miss: "venue",
  lineup_too_ambiguous: "artist",
  artist_ambiguous_match: "artist",
  artist_spotify_miss: "artist",
  artist_spotify_no_image: "artist",
  artist_low_confidence: "artist",
  movie_tmdb_miss: "movie",
  movie_low_confidence: "movie",
  movie_ambiguous_match: "movie",
  play_ambiguous_match: "play",
  play_no_image_found: "play",
  person_ambiguous_match: "person",
  low_score: "event",
  image_low_confidence: "image",
  enrichment_low_confidence: "enrichment",
  // Same collapse as `enrichment_low_confidence`, for the same reason: the real
  // entity (always `"artist"` today) is `hitl_context.problem.entity_type`, on
  // a different axis from this map.
  enrichment_curator_review: "enrichment",
  refresh_review: "refresh",
};

/**
 * The one human label per `hitl_reason`, and the only place they get one.
 *
 * Typed `Record<HitlReason, string>` on purpose: adding a member to
 * `HITL_REASONS` without labelling it here is a compile error. The filter used
 * to be a hand-maintained `string[]` inside `use-items-columns.tsx`, so a new
 * reason produced no error at all and the dropdown silently went stale.
 */
export const HITL_REASON_LABEL: Record<HitlReason, string> = {
  venue_no_address: "Venue — no address",
  venue_places_miss: "Venue — Places miss",
  venue_low_confidence: "Venue — low confidence",
  venue_geodata_miss: "Venue — geodata miss",
  lineup_too_ambiguous: "Lineup too ambiguous",
  artist_ambiguous_match: "Artist — ambiguous match",
  artist_spotify_miss: "Artist — Spotify miss",
  artist_spotify_no_image: "Artist — Spotify has no image",
  artist_low_confidence: "Artist — low confidence",
  movie_tmdb_miss: "Movie — TMDb miss",
  movie_low_confidence: "Movie — low confidence",
  movie_ambiguous_match: "Movie — ambiguous match",
  play_ambiguous_match: "Play — ambiguous match",
  play_no_image_found: "Play — no image found",
  person_ambiguous_match: "Person — ambiguous match",
  low_score: "Low score",
  image_low_confidence: "Image — low confidence",
  enrichment_low_confidence: "Enrichment — low confidence",
  enrichment_curator_review: "Curator review",
  refresh_review: "Refresh — review changes",
};

/**
 * The reasons only an ENRICHMENT job can park on — Tina's
 * `ENRICHMENT_HITL_REASONS`, exactly.
 *
 * Why the option lists are split at all: `list_events` ANDs `scrape_class_only()`
 * whenever both `content_type` and `entity_id` are absent, so one of these
 * offered in a scrape-class view is a filter that returns an empty page forever.
 * Prod agrees — zero scrape rows carry one, and none can (2026-08-27).
 *
 * `enrichment_curator_review` (artist-field-locks, 2026-09-28) joins it for the same
 * reason: it parks on the `enrichment_artist_curation` job, not on a scrape content type,
 * so a scrape-class view offering it is the identical empty-page-forever filter.
 */
export const ENRICHMENT_HITL_REASONS = [
  "enrichment_low_confidence",
  "enrichment_curator_review",
] as const satisfies readonly HitlReason[];

/**
 * The reasons only a REFRESH job can park on — tina's `_REFRESH_REASONS`, exactly.
 *
 * Held out of the scrape lists for the same reason {@link ENRICHMENT_HITL_REASONS} is:
 * `list_events`' `scrape_class_only()` excludes `music_event_refresh` rows alongside the
 * `enrichment_*` ones, so offering this reason in a scrape-class view is a filter that returns an
 * empty page forever. Refresh parks have their own surface — `/tina/refresh-review`, which reads
 * `v_refresh_jobs` — and are also reachable by drilling in from a refresh run's Refreshes tab.
 */
export const REFRESH_HITL_REASONS = [
  "refresh_review",
] as const satisfies readonly HitlReason[];

const isEnrichmentReason = (reason: HitlReason): boolean =>
  (ENRICHMENT_HITL_REASONS as readonly HitlReason[]).includes(reason);

/**
 * True for a reason only a refresh job can park on.
 *
 * Exported because it is no longer only an option-list predicate: `/tina/hitl` re-narrows its
 * own page with it, undoing the class widening `list_events` applies to a `status=hitl` read
 * (see {@link SCRAPE_HITL_REASON_OPTIONS}).
 */
export const isRefreshHitlReason = (
  reason: HitlReason | null | undefined,
): boolean =>
  !!reason && (REFRESH_HITL_REASONS as readonly HitlReason[]).includes(reason);

/**
 * `hitl_reason` options for a SCRAPE-class view — `/tina/items`' default column
 * set **and** `/tina/hitl`. Derived — never hand-listed, and never the whole
 * vocabulary: see {@link ENRICHMENT_HITL_REASONS}.
 *
 * The queue shares this list rather than widening it (owner ruling, superseding the IMS half of
 * R34): refresh parks are reviewed on `/tina/refresh-review`, and the queue drops them from its
 * page — so `refresh_review` there would be a filter over rows the queue itself hides.
 */
export const SCRAPE_HITL_REASON_OPTIONS: ReadonlyArray<
  FilterOption<HitlReason>
> = HITL_REASONS.filter(
  (value) => !isEnrichmentReason(value) && !isRefreshHitlReason(value),
).map((value) => ({
  label: HITL_REASON_LABEL[value],
  value,
}));

/** `hitl_reason` options for an enrichment-class view — the complement. */
export const ENRICHMENT_HITL_REASON_OPTIONS: ReadonlyArray<
  FilterOption<HitlReason>
> = ENRICHMENT_HITL_REASONS.map((value) => ({
  label: HITL_REASON_LABEL[value],
  value,
}));

/** `hitl_reason` options for a refresh-class view — the other complement. */
export const REFRESH_HITL_REASON_OPTIONS: ReadonlyArray<
  FilterOption<HitlReason>
> = REFRESH_HITL_REASONS.map((value) => ({
  label: HITL_REASON_LABEL[value],
  value,
}));

/** The one human label per `final_outcome`. Same compile-forced contract. */
export const FINAL_OUTCOME_LABEL: Record<FinalOutcome, string> = {
  created: "Created",
  draft: "Draft",
  manual_review: "Manual review",
  duplicate: "Duplicate",
  skipped: "Skipped",
  failed: "Failed",
  cancelled: "Cancelled",
  discovered: "Discovered",
  enriched: "Enriched",
  // The refresh five. Prefixed in the label too: these read beside the scrape
  // outcomes in the item column, and a bare "Unchanged" next to "Created" says
  // nothing about which pipeline produced it.
  refresh_unchanged: "Refresh — unchanged",
  refresh_updated: "Refresh — updated",
  refresh_dismissed: "Refresh — dismissed",
  refresh_shadow: "Refresh — shadow",
  refresh_retired: "Refresh — retired",
};

/**
 * The outcomes an enrichment job can terminate with.
 *
 * `enriched` / `manual_review` are the enrich stage's own two results; `failed`,
 * `skipped` and `cancelled` come from the generic worker transitions every
 * content type walks through. The other four — `created`, `draft`,
 * `duplicate`, `discovered` — are stamped by scrape create / dedup / discover
 * stages, and a job row runs none of them.
 */
export const ENRICHMENT_OUTCOMES = [
  "enriched",
  "manual_review",
  "failed",
  "skipped",
  "cancelled",
] as const satisfies readonly FinalOutcome[];

/**
 * The outcomes an event-refresh job can terminate with.
 *
 * The five `refresh_*` values are the refresh pipeline's own; `manual_review`, `failed`,
 * `skipped` and `cancelled` come from the generic worker transitions every content type walks.
 * `manual_review` in particular is what a `refresh_review` park stamps — there is no
 * `refresh_hitl` outcome, which is why the Refreshes tab reads `v_refresh_jobs.refresh_outcome`
 * (where the park does read back as `"hitl"`) rather than `final_outcome`.
 */
export const REFRESH_OUTCOMES = [
  "refresh_unchanged",
  "refresh_updated",
  "refresh_dismissed",
  "refresh_shadow",
  "refresh_retired",
  "manual_review",
  "failed",
  "skipped",
  "cancelled",
] as const satisfies readonly FinalOutcome[];

const REFRESH_OUTCOME_SET: ReadonlySet<string> = new Set(
  REFRESH_OUTCOMES.filter((value) => value.startsWith("refresh_")),
);

/**
 * `final_outcome` options for a SCRAPE-class view.
 *
 * `enriched` is the one outcome no scrape row can carry — only the enrichment
 * tail writes it — so offering it beside a `scrape_class_only()` default is a
 * guaranteed empty page (0 scrape rows in prod, 2026-08-27). Same split, same
 * reason, as {@link SCRAPE_HITL_REASON_OPTIONS}.
 */
export const SCRAPE_OUTCOME_OPTIONS: ReadonlyArray<FilterOption<FinalOutcome>> =
  FINAL_OUTCOMES.filter(
    (value) => value !== "enriched" && !REFRESH_OUTCOME_SET.has(value),
  ).map((value) => ({
    label: FINAL_OUTCOME_LABEL[value],
    value,
  }));

/** `final_outcome` options for an enrichment-class view. */
export const ENRICHMENT_OUTCOME_OPTIONS: ReadonlyArray<
  FilterOption<FinalOutcome>
> = ENRICHMENT_OUTCOMES.map((value) => ({
  label: FINAL_OUTCOME_LABEL[value],
  value,
}));

/** `final_outcome` options for a refresh-class view. */
export const REFRESH_OUTCOME_OPTIONS: ReadonlyArray<
  FilterOption<FinalOutcome>
> = REFRESH_OUTCOMES.map((value) => ({
  label: FINAL_OUTCOME_LABEL[value],
  value,
}));

export const TINA_POLL_INTERVAL_MS = 3000;
